import { useEffect, useState } from "react";
import cx from "classnames";
import { faFilter } from "@fortawesome/pro-regular-svg-icons";

import { parseNumber } from "common/utils/numbers";
import { useMatch } from "common/router/hooks";
import Spinner from "common/components/Spinner";
import Icon from "common/components/Icon";
import { FetchError } from "common/fetch/types";
import { Optional, WithStringValues } from "common/utils/types";
import { useListSearchState } from "common/hooks";
import { Feature } from "common/types";

import Page from "client/common/components/layouts/Page";
import Container from "client/common/components/layouts/Container";
import Pagination from "client/common/components/layouts/Pagination";
import Results from "client/common/components/results/Results";
import LoadingPage from "client/common/components/loading/LoadingPage";
import Error404Page from "client/common/components/errors/Error404Page";
import { useClinic, useFeatures } from "client/clinics/hooks";
import Body from "client/common/components/layouts/Body";
import { Paths } from "client/routes";
import PillButton from "client/common/components/buttons/PillButton";
import { useBreakpoints } from "client/common/hooks";
import { useProductLinesViewOnVisible, useViewItemListOnVisible } from "client/analytics/utils";
import { ProductLineListSource } from "client/analytics/types";

import ProductLineCard from "./ProductLineCard";
import CategoryContentLookup from "./CategoryContent";
import { iconForCategorySlug } from "../constants/categoryIcons";
import {
  useFilterPanel,
  useProductLineSearchQuery,
  useCategoryQuery,
  useCategoryProductLineFilterOptionState,
} from "../hooks";
import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_PAGE_SIZE_WITH_PANEL,
  CATEGORY_PRODUCT_LINE_FILTER_DEFINITIONS,
} from "../constants/filterDefinitions";
import { getCategoryProductLineFiltersFromSearch, getSearchString } from "../utils";
import BrowseSupport from "./BrowseSupport";
import PreFooter from "./PreFooter";
import AppliedFilterTags from "./AppliedFilterTags";
import ProductLineSearchFilterModal from "./CategoryProductLineSearchFilterModal";
import FilterLayout from "./FilterLayout";
import FilterPanel from "./FilterPanel";
import {
  ProductLineSearch,
  ProductLineSearchFilters,
  SelectedCategoryProductLineFilters,
} from "../types";

import styles from "../styles/CategoryPage.module.less";

type CategorySearchUpdate = Partial<WithStringValues<Paths["products"]["category"]["Search"]>>;

export const initialProductLineFilters = {
  manufacturer_ids: [],
  species: [],
  prescription_required: [],
};

export const initialSearch = {
  ...initialProductLineFilters,
  page: 1,
};

type CategoryProductLinesProps = {
  id: number;
  productLines: Optional<ProductLineSearch>;
  isLoading: boolean;
  isRefetching: boolean;
  error: Optional<FetchError>;
  filters: SelectedCategoryProductLineFilters;
  onSearchChange: (search: CategorySearchUpdate) => void;
  onToggleFilterModal: () => void;
};

const CategoryProductLines = ({
  id,
  productLines,
  isLoading,
  isRefetching,
  error,
  filters,
  onSearchChange,
  onToggleFilterModal,
}: CategoryProductLinesProps) => {
  const { isPhoneAndSmaller } = useBreakpoints();
  const { data: filterState } = useCategoryProductLineFilterOptionState([id]);
  const itemListRef = useViewItemListOnVisible(
    productLines?.results,
    ProductLineListSource.CategoryPage,
  );
  const productLinesViewRef = useProductLinesViewOnVisible(
    productLines?.results,
    ProductLineListSource.CategoryPage,
    id,
  );

  const isFiltersV2 = useFeatures()[Feature.StorefrontFiltersV2];

  const panel = useFilterPanel<SelectedCategoryProductLineFilters, ProductLineSearchFilters>({
    definitions: CATEGORY_PRODUCT_LINE_FILTER_DEFINITIONS,
    response: productLines,
    cachedFilterOptions: filterState?.filters,
    filters,
    onChange: onSearchChange,
  });
  const { filterOptions, hasCompleteOptions, showFilterPanel, hasSelectedFilters } = panel;
  const hasAnyFilterOptions = Object.values(filterOptions ?? {}).some((options) => options.length);

  const renderContent = () => {
    if (isLoading) {
      return <Results.Loading description="Loading results" />;
    }

    if (!productLines || error) {
      return <Results.FetchError error={error} entityName="results" />;
    }

    if (!productLines.results.length) {
      return (
        <Results.None
          header="No products found"
          description={
            hasCompleteOptions && hasSelectedFilters
              ? "No products match the filters you have applied."
              : undefined
          }
        />
      );
    }

    return (
      <div ref={itemListRef} className={styles.search}>
        <div className={cx(styles.products, showFilterPanel && styles.productsNarrow)}>
          {productLines.results.map((productLine, index) => (
            <div
              key={productLine.id}
              ref={index === 0 ? productLinesViewRef : undefined}
              className={styles.product}
            >
              {isPhoneAndSmaller ? (
                <ProductLineCard.Compact
                  productLine={productLine}
                  eventViewName={ProductLineListSource.CategoryPage}
                  eventCategoryId={id}
                />
              ) : (
                <ProductLineCard
                  productLine={productLine}
                  eventViewName={ProductLineListSource.CategoryPage}
                  eventCategoryId={id}
                />
              )}
            </div>
          ))}
        </div>
        <Pagination
          paginator={productLines.paginator}
          onPageChange={(page) => onSearchChange({ page })}
          entityName="result"
        />
      </div>
    );
  };

  return (
    <FilterLayout panel={showFilterPanel && <FilterPanel.Rail {...panel} />}>
      <div className={styles.content}>
        <div className={styles.info}>
          <div className={styles.label}>
            {isRefetching ? <Spinner /> : getSearchString(productLines?.paginator)}
          </div>
          {filterOptions && hasAnyFilterOptions && !showFilterPanel && (
            <div className={styles.actions}>
              <AppliedFilterTags<SelectedCategoryProductLineFilters, ProductLineSearchFilters>
                definitions={CATEGORY_PRODUCT_LINE_FILTER_DEFINITIONS}
                options={filterOptions}
                value={filters}
                onChange={(next) => onSearchChange({ ...filters, ...next })}
                variant={isFiltersV2 ? "accent" : "primary"}
              />
              <PillButton variant="secondary" size="s" type="button" onClick={onToggleFilterModal}>
                <span className={styles.filter}>
                  <Icon icon={faFilter} /> Filter
                </span>
              </PillButton>
            </div>
          )}
        </div>
        <FilterPanel.AppliedRow {...panel} />
        {renderContent()}
      </div>
    </FilterLayout>
  );
};

const CategoryPage = () => {
  const match = useMatch<Paths["products"]["category"]>();
  const clinic = useClinic();
  const features = useFeatures();
  const isCarouselV2 = features[Feature.CarouselV2];
  const parsedId = parseNumber(match.params.id, 0);

  const [search, setSearch] = useListSearchState<Paths["products"]["category"]>({
    page: 1,
    ...initialProductLineFilters,
  });

  const { data: filterState } = useCategoryProductLineFilterOptionState([parsedId]);

  const [isFiltering, setIsFiltering] = useState(false);

  const { data: category, isLoading: isCategoryLoading } = useCategoryQuery(parsedId);

  const filters = getCategoryProductLineFiltersFromSearch(search);

  const {
    data: productLines,
    error: productLineError,
    isRefetching: isRefetchingProductLines,
    isLoading: isLoadingProductLines,
  } = useProductLineSearchQuery({
    ...filters,
    category_ids: [parsedId],
    important_first: true,
    page_size: features[Feature.StorefrontFiltersV2]
      ? DEFAULT_PAGE_SIZE_WITH_PANEL
      : DEFAULT_PAGE_SIZE,
    q: "",
    sort: "",
    page: parseNumber(search.page, 1),
  });

  // Follows the response, not the flag: the two disagree for a page after the flag flips.
  const hasCompleteFilterOptions = !!productLines?.has_complete_filter_options;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [search.page]);

  if (isCategoryLoading) {
    return <LoadingPage />;
  }

  if (!category) {
    return <Error404Page />;
  }

  const CategoryContent = CategoryContentLookup[category.slug] ?? CategoryContentLookup.default;

  return (
    <Page
      style={{
        background: `radial-gradient(farthest-corner at 30% 20%, ${CategoryContent.gradient.start} 0%, ${CategoryContent.gradient.stop} 50%)`,
      }}
      className={styles.page}
    >
      <div>
        <Container>
          <div className={styles.top}>
            <div className={styles.heading}>
              <h1 className={styles.title}>{category.extended_name}</h1>
              <div className={styles.description}>{category.description}</div>
            </div>
            {isCarouselV2 ? (
              <Icon className={styles.icon} icon={iconForCategorySlug(category.slug)} />
            ) : (
              <CategoryContent.Icon className={styles.icon} />
            )}
          </div>
        </Container>
        <div className={styles.main}>
          <Body>
            <CategoryProductLines
              id={parsedId}
              productLines={productLines}
              filters={filters}
              isLoading={isLoadingProductLines}
              isRefetching={isRefetchingProductLines}
              error={productLineError}
              onSearchChange={setSearch}
              onToggleFilterModal={() => setIsFiltering(!isFiltering)}
            />
            {filterState && (
              <ProductLineSearchFilterModal
                options={
                  hasCompleteFilterOptions && productLines
                    ? productLines.filters
                    : filterState.filters
                }
                showCounts={hasCompleteFilterOptions}
                isCollapsible={hasCompleteFilterOptions}
                isVisible={isFiltering}
                onClose={() => setIsFiltering(false)}
                value={filters}
                onChange={setSearch}
              />
            )}
          </Body>
        </div>
        <div className={styles.support}>
          <Container>
            <BrowseSupport />
          </Container>
        </div>
      </div>
      <PreFooter className={styles.preFooter} image={clinic.storefront_prefooter_image} />
    </Page>
  );
};

export default CategoryPage;
